import React, { useState } from 'react';
import { Link } from '../catalyst/link';
import { Copy, Check } from 'lucide-react';

interface ResourceLinkProps {
	/** Full resource UUID */
	id: string;
	/** If provided, the truncated ID is rendered as a navigable link */
	href?: string;
	/** Human-friendly display name shown above the ID row */
	name?: string | null;
	className?: string;
}

/**
 * Standardized resource reference widget.
 *
 * Renders:
 *   [Name]        ← optional human-friendly label
 *   [abc123 📋]  ← full ID (linked if href given) + clipboard copy button
 */
export default function ResourceLink({ id, href, name, className }: ResourceLinkProps) {
	const [copied, setCopied] = useState(false);

	const handleCopy = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		navigator.clipboard.writeText(id).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	};

	return (
		<span className={`inline-flex flex-col gap-0.5 ${className ?? ''}`}>
			{name && (
				<span className="text-sm font-medium text-zinc-900 dark:text-white">{name}</span>
			)}
			<span className="inline-flex items-center gap-1">
				{href ? (
					<Link
						href={href}
						onClick={(e) => e.stopPropagation()}
						className="font-mono text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
					>
						{id}
					</Link>
				) : (
					<span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">{id}</span>
				)}
				<button
					type="button"
					onClick={handleCopy}
					className="inline-flex items-center text-zinc-400 transition-colors hover:text-zinc-600 dark:hover:text-zinc-300"
					title={`Copy ID: ${id}`}
					aria-label="Copy ID to clipboard"
				>
					{copied ? (
						<Check className="h-3 w-3 text-green-500" />
					) : (
						<Copy className="h-3 w-3" />
					)}
				</button>
			</span>
		</span>
	);
}
